import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MessageCircle, FileText, Hammer, Rocket, ArrowRight, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { siteConfig } from '@/config/site'
import { SEO } from '@/components/SEO'

const stepIcons = [MessageCircle, FileText, Hammer, Rocket]

export function Process() {
  return (
    <>
      <SEO
        title="How It Works - Our Simple 4-Step Process"
        description="Getting your business online is easy with NEXAIGEN. Our simple 4-step process: Free Chat, Custom Plan, We Build It, Launch & Support. No surprises, no jargon."
        keywords={[
          'web design process',
          'how website development works',
          'website project timeline',
          'web design consultation',
          'website development steps',
        ]}
        url="https://nexaigen.co.uk/process"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pt-32 pb-20 container bg-background min-h-screen"
      >
        {/* Header */}
      <div className="max-w-4xl mx-auto mb-24 text-center">
        <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
          How It <span className="text-secondary">Works</span>
        </h1>
        <p className="text-xl text-slate-400">
          Getting your business online should be easy. Here is our simple 4-step process.
          No complicated jargon. No surprises.
        </p>
      </div>

      {/* Steps */}
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {siteConfig.process.map((step, i) => {
            const Icon = stepIcons[i] || MessageCircle
            return (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-8 md:p-10 rounded-3xl border border-white/5 bg-slate-900/50 backdrop-blur-sm hover:border-primary/30 transition-all h-full">
                  {/* Step Number */}
                  <div className="text-7xl font-black text-slate-800/50 absolute top-4 right-6 group-hover:text-primary/20 transition-colors select-none">
                    {step.step}
                  </div>

                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform">
                    <Icon size={32} />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{step.title}</h3>
                  <p className="text-slate-400 leading-relaxed text-lg">{step.description}</p>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* What You Can Expect */}
      <div className="mt-32 max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-white text-center mb-12">
          What You Can <span className="text-primary">Expect</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            'Clear communication at every step',
            'Regular updates on progress',
            'No technical jargon or confusing terms',
            'Fixed prices with no hidden fees',
            'Quick responses to your questions',
            'Training so you can manage your site',
            'Ongoing support after launch',
            'A team that genuinely cares',
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/30 border border-white/5"
            >
              <CheckCircle size={24} className="text-accent shrink-0" />
              <span className="text-slate-300">{item}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-32 max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-white text-center mb-6">
          How Long Does It <span className="text-primary">Take?</span>
        </h2>
        <p className="text-xl text-slate-400 text-center mb-12">
          Every project is different, but here is a rough guide:
        </p>

        <div className="space-y-4">
          {[
            { service: 'Simple Business Website', time: '2-3 weeks' },
            { service: 'E-commerce / Online Shop', time: '4-6 weeks' },
            { service: 'Mobile App', time: '6-8 weeks' },
            { service: 'SEO & Marketing', time: 'Ongoing monthly' },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex justify-between items-center p-6 rounded-2xl bg-slate-900/50 border border-white/5"
            >
              <span className="text-white font-medium text-lg">{item.service}</span>
              <span className="text-primary font-bold">{item.time}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-32 max-w-3xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Get Started?
        </h2>
        <p className="text-xl text-slate-400 mb-10">
          Book a free chat with us. We will discuss your needs, answer your questions,
          and give you a clear plan for your project.
        </p>
        <Link to="/contact">
          <Button size="lg">
            Book Your Free Chat <ArrowRight size={18} />
          </Button>
        </Link>
      </div>
      </motion.div>
    </>
  )
}

export default Process
